import { Search, Download, Loader2, Package, Tag, Users, Star, ScanLine, Crown, Filter } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { MRRTrendChart, UserGrowthChart } from './Charts'
import { UserGraph } from './UserGraph'
import { PMDashboard } from './pm'
import { UsersView, BrandsView, ProductsView, ReviewsView, AnalyticsView, LogoSearchView, ContentView, BetaFeedbackView, ActivityView, UserGraphView, ProjectsView, RecallsView, BlogPostsView, WebbOverviewTab } from './views'
import { useOrg } from '@/lib/orgs'
import { useOrgMetrics, useUserStats } from '@/lib/data'

export function DashboardCompany() {
  const { activeOrg, currentTab, getOrgClient } = useOrg()

  // Get the Supabase client for this org
  const orgClient = activeOrg?.isConfigured ? getOrgClient(activeOrg.slug) : null

  // Fetch metrics (Buy'r only — Webb uses its own hooks via WebbOverviewTab)
  const isBuyr = activeOrg?.slug === 'buyr'
  const { metrics, isLoading: metricsLoading } = useOrgMetrics(isBuyr ? orgClient : null)
  const userStats = useUserStats(isBuyr ? orgClient : null)

  if (!activeOrg) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <Loader2 size={48} className="mb-4 animate-spin" />
        <p className="text-lg font-medium">Loading organization...</p>
      </div>
    )
  }

  const OrgIcon = activeOrg.icon

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border)] mr-4 shadow-lg shadow-black/20">
            <OrgIcon size={28} className={activeOrg.color} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-main)]">{activeOrg.name}</h1>
            <p className="text-[var(--text-muted)] text-sm flex items-center">
              {activeOrg.isConfigured ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Connected to Supabase
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-yellow-500 mr-2" />
                  Pending Configuration
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors">
            <Search size={20} />
          </button>
          <button className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors">
            <Download size={20} />
          </button>
          <button className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 text-sm font-medium">
            Manage Access
          </button>
        </div>
      </div>

      {/* Dynamic Content */}
      <div>
        {renderTabContent(currentTab, activeOrg, metrics, metricsLoading, userStats, orgClient)}
      </div>
    </div>
  )
}

interface RenderProps {
  metrics: ReturnType<typeof useOrgMetrics>['metrics']
  metricsLoading: boolean
  userStats: ReturnType<typeof useUserStats>
}

function renderTabContent(
  tab: string,
  org: { name: string; icon: React.ElementType; color: string; slug: string; isConfigured: boolean },
  metrics: RenderProps['metrics'],
  metricsLoading: boolean,
  userStats: RenderProps['userStats'],
  orgClient: SupabaseClient | null
) {
  const OrgIcon = org.icon

  switch (tab) {
    case 'overview':
      if (org.slug === 'webb') {
        return <WebbOverviewTab client={orgClient} />
      }
      return (
        <div className="space-y-6 animate-fade-in pb-8">
          {/* Real-time connection status */}
          {org.isConfigured && (
            <div className="flex items-center gap-2 text-sm">
              <span className={`w-2 h-2 rounded-full ${userStats.isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-yellow-500'}`} />
              <span className="text-[var(--text-muted)]">
                {userStats.isConnected ? 'Live data connected' : 'Connecting...'}
              </span>
              {userStats.recentUsers.last24Hours > 0 && (
                <span className="text-[var(--text-muted)]">
                  &bull; {userStats.recentUsers.last24Hours} new users today
                </span>
              )}
            </div>
          )}

          {/* Main metrics grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <MetricCard
              icon={Package}
              label="Total Products"
              value={userStats.totalProducts.toLocaleString()}
              subtitle="In database"
              iconColor="text-emerald-400"
              bgGradient="from-emerald-500/15 to-emerald-400/5"
            />
            <MetricCard
              icon={Tag}
              label="Total Brands"
              value={userStats.totalBrands.toLocaleString()}
              subtitle="Registered brands"
              iconColor="text-amber-400"
              bgGradient="from-amber-500/15 to-amber-400/5"
            />
            <MetricCard
              icon={Users}
              label="Total Users"
              value={userStats.totalUsers.toLocaleString()}
              subtitle={`${userStats.recentUsers.last1Hour} joined last hour`}
              iconColor="text-blue-400"
              bgGradient="from-blue-500/15 to-blue-400/5"
            />
            <MetricCard
              icon={Star}
              label="Total Reviews"
              value={userStats.totalReviews.toLocaleString()}
              subtitle="User reviews"
              iconColor="text-yellow-400"
              bgGradient="from-yellow-500/15 to-yellow-400/5"
            />
            <MetricCard
              icon={ScanLine}
              label="Total Scans"
              value={userStats.totalScans.toLocaleString()}
              subtitle="Product scans"
              iconColor="text-purple-400"
              bgGradient="from-purple-500/15 to-purple-400/5"
            />
            <MetricCard
              icon={Crown}
              label="Premium Users"
              value={userStats.premiumUsers.toLocaleString()}
              subtitle={`${userStats.premiumMonthly} monthly / ${userStats.premiumYearly} yearly`}
              iconColor="text-[var(--primary)]"
              bgGradient="from-[var(--primary)]/15 to-[var(--primary)]/5"
            />
          </div>

          {/* Premium breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
              <p className="text-[var(--text-muted)] text-sm">Monthly This Month</p>
              <p className="text-2xl font-bold text-[var(--text-main)] mt-1">{userStats.monthlyThisMonth}</p>
              <p className="text-xs text-[var(--text-muted)]">avg: {userStats.avgMonthlyAcquisition}/mo</p>
            </div>
            <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
              <p className="text-[var(--text-muted)] text-sm">Yearly This Month</p>
              <p className="text-2xl font-bold text-[var(--text-main)] mt-1">{userStats.yearlyThisMonth}</p>
              <p className="text-xs text-[var(--text-muted)]">avg: {userStats.avgYearlyAcquisition}/mo</p>
            </div>
            <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
              <p className="text-[var(--text-muted)] text-sm">Last 30 Min</p>
              <p className="text-2xl font-bold text-[var(--text-main)] mt-1">{userStats.recentUsers.last30Min}</p>
              <p className="text-xs text-[var(--text-muted)]">new signups</p>
            </div>
            <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
              <p className="text-[var(--text-muted)] text-sm">Last 24 Hours</p>
              <p className="text-2xl font-bold text-[var(--text-main)] mt-1">{userStats.recentUsers.last24Hours}</p>
              <p className="text-xs text-[var(--text-muted)]">new signups</p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-[var(--bg-card)] border border-[var(--border)] p-6 rounded-xl h-[400px]">
              <h3 className="text-[var(--text-main)] font-semibold mb-4">MRR Trend (12 Months)</h3>
              <p className="text-[var(--text-muted)] text-xs mb-4">Monthly Recurring Revenue from premium subscriptions</p>
              <div className="h-[280px] w-full">
                <MRRTrendChart data={userStats.revenueHistory} />
              </div>
            </div>
            <div className="bg-[var(--bg-card)] border border-[var(--border)] p-6 rounded-xl h-[400px]">
              <h3 className="text-[var(--text-main)] font-semibold mb-4">User Growth (30 Days)</h3>
              <p className="text-[var(--text-muted)] text-xs mb-4">Cumulative user count over the last 30 days</p>
              <div className="h-[280px] w-full">
                <UserGrowthChart data={userStats.userGrowthData} />
              </div>
            </div>
          </div>
        </div>
      )

    case 'analytics':
      return <AnalyticsView client={orgClient} />

    case 'user-graph':
      return <UserGraphView client={orgClient} />

    case 'projects':
      return <ProjectsView orgSlug={org.slug} />

    case 'products':
      return <ProductsView client={orgClient} />

    case 'brands':
      return <BrandsView client={orgClient} />

    case 'users':
      return <UsersView client={orgClient} />

    case 'reviews':
      return <ReviewsView client={orgClient} />

    case 'logo-search':
      return <LogoSearchView client={orgClient} />

    case 'content':
      return <ContentView client={orgClient} />

    case 'beta-feedback':
      return <BetaFeedbackView client={orgClient} />

    case 'activity':
      return <ActivityView client={orgClient} />

    case 'recalls':
      return <RecallsView client={orgClient} />

    case 'blog':
      return <BlogPostsView client={orgClient} />

    case 'settings':
      return (
        <div className="space-y-6 animate-fade-in">
          <h2 className="text-xl font-bold text-[var(--text-main)]">Organization Settings</h2>
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
            <h3 className="text-lg font-semibold text-[var(--text-main)] mb-4">General</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[var(--text-muted)] mb-2">Organization Name</label>
                <input
                  type="text"
                  defaultValue={org.name}
                  className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
              <div>
                <label className="block text-sm text-[var(--text-muted)] mb-2">Description</label>
                <textarea
                  rows={3}
                  className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
            </div>
          </div>
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
            <h3 className="text-lg font-semibold text-[var(--text-main)] mb-4">Supabase Connection</h3>
            <p className="text-[var(--text-muted)] text-sm">
              Configure the Supabase connection for this organization in the admin settings.
            </p>
          </div>
        </div>
      )

    default:
      return (
        <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
          <Filter size={48} className="mb-4 opacity-20" />
          <p className="text-lg font-medium">Coming Soon</p>
          <p className="text-sm">The {tab.replace('-', ' ')} view is currently under development.</p>
        </div>
      )
  }
}

// Reusable metric card component
interface MetricCardProps {
  icon: React.ElementType
  label: string
  value: string
  subtitle: string
  iconColor: string
  bgGradient: string
}

function MetricCard({ icon: Icon, label, value, subtitle, iconColor, bgGradient }: MetricCardProps) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] p-6 rounded-xl hover:border-[var(--text-muted)]/30 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-[var(--text-muted)] text-sm font-medium">{label}</h3>
          <div className="mt-2">
            <span className="text-3xl font-bold text-[var(--text-main)]">{value}</span>
          </div>
          <p className="text-sm text-[var(--text-muted)] mt-1">{subtitle}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${bgGradient} flex items-center justify-center`}>
          <Icon size={24} className={iconColor} />
        </div>
      </div>
    </div>
  )
}
